import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Target, TrendingUp, CheckCircle2, Calendar, AlertCircle, Moon, AlertTriangle, CalendarPlus, Clock, Settings, LogOut, User, MoreVertical, Check, CalendarDays, X } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ThemeToggle from '../components/ThemeToggle';
import TimeStats from '../components/TimeStats';
import QuestionsCountdown from '../components/QuestionsCountdown';
import QuickStudyMode from '../components/QuickStudyMode';
import GapFillingWidget from '../components/GapFillingWidget';
import StudySelector from '../components/StudySelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reviewTopics, setReviewTopics] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  const [inProgressTopics, setInProgressTopics] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, topicsRes, alertsRes] = await Promise.all([
        api.get('/stats/weekly'),
        api.get('/topics'),
        api.get('/analytics/alerts')
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data || []);
      
      // Encontrar tópicos em progresso (1 ou 2 etapas completas)
      const inProgress = topicsRes.data.filter(topic => {
        const completedStages = [topic.reading_done, topic.deep_dive_done, topic.questions_done].filter(Boolean).length;
        return completedStages > 0 && completedStages < 3;
      });
      setInProgressTopics(inProgress);
      
      const topicsWithReviews = topicsRes.data.filter(t => t.review_dates && t.review_dates.length > 0);
      const upcomingReviews = [];
      
      topicsWithReviews.forEach(topic => {
        const completedReviews = topic.completed_reviews || [];
        const skippedReviews = topic.skipped_reviews || [];
        
        topic.review_dates.forEach((date, index) => {
          const reviewDate = parseISO(date);
          const isCompleted = completedReviews.includes(index);
          const isSkipped = skippedReviews.includes(index);
          
          upcomingReviews.push({
            ...topic,
            reviewDate: date,
            reviewIndex: index,
            reviewInterval: ['24h', '7d', '15d', '30d'][index],
            isPast: isPast(reviewDate) && !isToday(reviewDate),
            isCompleted: isCompleted,
            isSkipped: isSkipped
          });
        });
      });
      
      // Contar revisões atrasadas (apenas não concluídas e não puladas)
      const overdue = upcomingReviews.filter(r => r.isPast && !r.isCompleted && !r.isSkipped).length;
      setOverdueCount(overdue);
      
      // Filtrar revisões não concluídas e não puladas para exibição
      const pendingReviews = upcomingReviews.filter(r => !r.isCompleted && !r.isSkipped);
      pendingReviews.sort((a, b) => new Date(a.reviewDate) - new Date(b.reviewDate));
      setReviewTopics(pendingReviews.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Ações rápidas para revisões com atualização imediata (Optimistic UI)
  const handleReviewAction = async (topicId, reviewIndex, action) => {
    // Guardar referência ao item sendo removido ANTES de modificar o estado
    const reviewBeingRemoved = reviewTopics.find(r => r.id === topicId && r.reviewIndex === reviewIndex);
    
    // PASSO 1: Atualização otimista IMEDIATA - remover da lista local ANTES da API
    setReviewTopics(prevReviews => {
      const filtered = prevReviews.filter(r => !(r.id === topicId && r.reviewIndex === reviewIndex));
      console.log('[Optimistic UI] Removendo revisão da lista:', { topicId, reviewIndex, prevCount: prevReviews.length, newCount: filtered.length });
      return filtered;
    });
    
    // PASSO 2: Recalcular contador de atrasados imediatamente
    if (reviewBeingRemoved?.isPast) {
      setOverdueCount(prev => Math.max(0, prev - 1));
    }

    // PASSO 3: Fazer chamada API em background (não bloqueia UI)
    try {
      const response = await api.post(`/topics/${topicId}/review-action`, {
        review_index: reviewIndex,
        action: action
      });
      toast.success(response.data.message);
      
      // PASSO 4: Re-fetch com delay para sincronização (não sobrescreve UI imediatamente)
      setTimeout(() => {
        fetchDashboardData();
      }, 500);
    } catch (error) {
      console.error('Error performing review action:', error);
      toast.error('Erro ao executar ação. Recarregando...');
      // Em caso de erro, re-fetch para restaurar estado correto
      fetchDashboardData();
    }
  };

  const formatReviewDate = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Hoje';
    if (isTomorrow(date)) return 'Amanhã';
    return format(date, "dd 'de' MMMM", { locale: ptBR });
  };

  const handleReschedule = async () => {
    try {
      setIsRescheduling(true);
      const response = await api.post('/topics/reschedule');
      toast.success(`${response.data.rescheduled_count} temas reagendados!`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Erro ao reagendar temas');
    } finally {
      setIsRescheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard-page">
      <div className="max-w-md mx-auto md:max-w-5xl px-6 md:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Olá, {user?.name}</h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">Acompanhe seu progresso semanal</p>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="settings-menu-btn">
                  <Settings className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {user?.name}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  Configurações
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/schedule/manage')} className="cursor-pointer">
                  <Calendar className="mr-2 h-4 w-4" />
                  Gerenciar Agenda
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout} 
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Alerta de Revisões Atrasadas */}
        {overdueCount > 0 && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-lg" data-testid="overdue-alert">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-semibold text-red-800 dark:text-red-200">
                  Você tem {overdueCount} revisão{overdueCount > 1 ? 'ões' : ''} atrasada{overdueCount > 1 ? 's' : ''}!
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">Confira abaixo em "Próximas Revisões"</p>
              </div>
            </div>
          </div>
        )}

        {/* Botões de Ação */}
        <div className="mb-6 flex gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={handleReschedule}
            disabled={isRescheduling}
            className="gap-2"
            data-testid="reschedule-btn"
          >
            <Moon className="w-4 h-4" />
            {isRescheduling ? 'Reagendando...' : 'Não estudei hoje'}
          </Button>
          
          <Button
            onClick={() => navigate('/schedule/manage')}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="add-schedule-btn"
          >
            <CalendarPlus className="w-4 h-4" />
            Adicionar Cronograma
          </Button>
        </div>

        {/* Performance Alerts */}
        {alerts.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="performance-alerts">
            {alerts.map((alert, index) => (
              <Card 
                key={index}
                className={`border-l-4 ${
                  alert.severity === 'critical' 
                    ? 'border-l-red-500 bg-red-50 dark:bg-red-900/10' 
                    : 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10'
                }`}
                data-testid={`alert-${alert.severity}-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                      alert.severity === 'critical' ? 'text-red-600' : 'text-blue-600'
                    }`} />
                    <div className="flex-1">
                      <h3 className={`font-semibold mb-1 ${
                        alert.severity === 'critical' ? 'text-red-900 dark:text-red-200' : 'text-blue-900 dark:text-blue-200'
                      }`}>
                        {alert.message}
                      </h3>
                      <p className={`text-sm mb-3 ${
                        alert.severity === 'critical' ? 'text-red-700 dark:text-red-300' : 'text-blue-700 dark:text-blue-300'
                      }`}>
                        {alert.recommendation}
                      </p>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="font-medium">
                          {alert.total_questions} questões realizadas
                        </span>
                        <span className="font-medium">
                          {alert.total_wrong} erros
                        </span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/analytics')}
                      className="shrink-0"
                    >
                      Ver Detalhes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Estudos em Progresso */}
        {inProgressTopics.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/10" data-testid="in-progress-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-0.5 text-amber-600" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-2 text-amber-900 dark:text-amber-200">
                    ⏳ {inProgressTopics.length} {inProgressTopics.length === 1 ? 'estudo em progresso' : 'estudos em progresso'}
                  </h3>
                  <div className="space-y-2">
                    {inProgressTopics.slice(0, 3).map(topic => {
                      const completedStages = [topic.reading_done, topic.deep_dive_done, topic.questions_done].filter(Boolean).length;
                      return (
                        <div 
                          key={topic.id} 
                          className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors"
                          onClick={() => navigate(`/topics/${topic.id}`)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 dark:text-slate-100">{topic.title}</span>
                            <span className="text-xs px-2 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-200 rounded-full">
                              {completedStages}/3 etapas
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">{topic.specialty}</span>
                        </div>
                      );
                    })}
                    {inProgressTopics.length > 3 && (
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        + {inProgressTopics.length - 3} outros tópicos em progresso
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <Card className="col-span-1 md:col-span-8 shadow-md hover:shadow-lg transition-all border-2 border-blue-200 dark:border-blue-800" data-testid="weekly-progress-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl text-slate-900 dark:text-slate-100">
                <Target className="w-6 h-6 text-blue-600" />
                Meta Semanal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Progresso</span>
                  <span className="text-4xl font-bold text-slate-900 dark:text-slate-100">
                    {stats?.total_questions || 0} <span className="text-xl text-slate-600 dark:text-slate-400">/ {stats?.goal || 300}</span>
                  </span>
                </div>
                <Progress value={Math.min(stats?.goal_progress || 0, 100)} className="h-3" data-testid="progress-bar" />
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-2">{Math.round(stats?.goal_progress || 0)}% completado</p>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t-2 border-blue-100">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600 dark:text-green-500" data-testid="correct-count">{stats?.total_correct || 0}</p>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1">Acertos</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600 dark:text-red-500" data-testid="wrong-count">{stats?.total_wrong || 0}</p>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1">Erros</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-500" data-testid="accuracy-rate">{stats?.correct_rate || 0}%</p>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1">Taxa de acerto</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-1 md:col-span-4 shadow-md hover:shadow-lg transition-all border-2 border-blue-200 dark:border-blue-800" data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={() => navigate('/schedule')} 
                className="w-full justify-start" 
                variant="outline"
                data-testid="view-schedule-button"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Ver Cronograma
              </Button>
              <Button 
                onClick={() => navigate('/topics/new')} 
                className="w-full justify-start"
                data-testid="add-topic-button"
              >
                Adicionar Tema
              </Button>
              <Button 
                onClick={() => navigate('/settings')} 
                className="w-full justify-start" 
                variant="outline"
                data-testid="settings-button"
              >
                Configurações
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Card Tempo Estudado */}
        <div className="mb-8">
          <TimeStats />
        </div>

        {/* Novos Widgets */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <QuestionsCountdown />
          <QuickStudyMode />
          <GapFillingWidget />
        </div>

        {/* Seletor de Estudos por Abordagem */}
        <div className="mb-8">
          <StudySelector />
        </div>

        <Card className="shadow-md border-2 border-blue-200 dark:border-blue-800" data-testid="reviews-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-slate-900 dark:text-slate-100">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Próximas Revisões
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reviewTopics.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Nenhuma revisão pendente</p>
                <p className="text-sm text-muted-foreground mt-1">Complete os estudos para ver as revisões aqui</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reviewTopics.map((review, index) => (
                  <div 
                    key={`${review.id}-${review.reviewIndex}`} 
                    className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                      review.isPast 
                        ? 'border-red-300 bg-red-50 dark:bg-red-900/20' 
                        : 'border-blue-200 bg-white dark:bg-slate-800'
                    } hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors`}
                    data-testid={`review-item-${index}`}
                  >
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/topics/${review.id}`)}
                    >
                      <h4 className="font-medium text-slate-900 dark:text-slate-100">{review.title}</h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        R{review.reviewIndex + 1} ({review.reviewInterval})
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-2">
                        <p className={`text-sm font-medium ${
                          review.isPast ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {review.isPast && <AlertCircle className="w-4 h-4 inline mr-1" />}
                          {formatReviewDate(review.reviewDate)}
                        </p>
                      </div>
                      {/* Botões de ação rápida VISÍVEIS */}
                      <div className="flex items-center gap-1">
                        {/* Concluir */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReviewAction(review.id, review.reviewIndex, 'complete');
                          }}
                          title="Concluir"
                          data-testid={`complete-review-${index}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        {/* Adiar +1 dia */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReviewAction(review.id, review.reviewIndex, 'postpone');
                          }}
                          title="Adiar +1 dia"
                          data-testid={`postpone-review-${index}`}
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                        {/* Pular/Ignorar */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReviewAction(review.id, review.reviewIndex, 'skip');
                          }}
                          title="Pular/Ignorar"
                          data-testid={`skip-review-${index}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
